// What the Cockpit's list pages share (docs/cockpit/spec.md): a toolbar of filters and a search field whose
// words the Application API filters by, a table of one page of rows, and a pager. The server filters, searches
// and pages; the page only asks for what is shown.
import { useEffect, useState, type ReactNode } from "react";
import { ApiError, type ListPage } from "../api.js";
import { formatDate } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { useWords } from "../i18n/words.js";

/** A list that could not be loaded says why in the person's language: `forbidden` when their Roles may not see it. */
export type Loaded<T> =
  | { readonly state: "loading" }
  | { readonly state: "ready"; readonly page: ListPage<T> }
  | { readonly state: "failed"; readonly forbidden: boolean };

/**
 * One page of a list from the Application API, loaded again whenever `filters` or the page number change.
 * `key` names the filters, so changing them goes back to page 1.
 */
export function useList<T, F extends object>(
  load: (filters: F & { page: number }) => Promise<ListPage<T>>,
  filters: F,
): { loaded: Loaded<T>; page: number; setPage: (page: number) => void } {
  const key = JSON.stringify(filters);
  const [asked, setAsked] = useState({ key, page: 1 });
  const page = asked.key === key ? asked.page : 1;
  const [loaded, setLoaded] = useState<Loaded<T>>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setLoaded({ state: "loading" });
    load({ ...(JSON.parse(key) as F), page }).then(
      (answer) => {
        if (live) setLoaded({ state: "ready", page: answer });
      },
      (error: unknown) => {
        if (live) setLoaded({ state: "failed", forbidden: error instanceof ApiError && error.status === 403 });
      },
    );
    return () => {
      live = false;
    };
  }, [load, key, page]);
  return { loaded, page, setPage: (next) => setAsked({ key, page: next }) };
}

/** A row of mutually exclusive filters, the chosen one pressed. */
export function Segments<V extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly (readonly [V, string])[];
  value: V;
  onChange: (value: V) => void;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map(([option, name]) => (
        <button key={option} type="button" aria-pressed={value === option} onClick={() => onChange(option)}>
          {name}
        </button>
      ))}
    </div>
  );
}

/** A search field for one list: its words go to the server when submitted. */
export function ListSearch({
  label,
  initial,
  onSearch,
}: {
  label: string;
  initial: string;
  onSearch: (words: string) => void;
}) {
  const w = useWords();
  const [words, setWords] = useState(initial);
  return (
    <form
      role="search"
      className="list-search"
      aria-label={label}
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(words.trim());
      }}
    >
      <label>
        <span className="sr-only">{label}</span>
        <input type="search" value={words} placeholder={label} onChange={(e) => setWords(e.target.value)} />
      </label>
      <button type="submit" className="btn">
        {w.listSearch}
      </button>
    </form>
  );
}

/** One page of a list: the table, what it shows of the total, and the pager. */
export function ListView<T>({
  loaded,
  what,
  empty,
  onPage,
  children,
}: {
  loaded: Loaded<T>;
  /** What the rows are, in the plural and the person's language: "Runs". */
  what: string;
  empty: string;
  onPage: (page: number) => void;
  children: (rows: readonly T[]) => ReactNode;
}) {
  const w = useWords();
  if (loaded.state === "loading") return <p role="status">{w.listLoading(what)}</p>;
  if (loaded.state === "failed")
    return <p role="alert">{loaded.forbidden ? w.listForbidden(what) : w.listFailed(what)}</p>;
  const { rows, total, page, pages } = loaded.page;
  if (total === 0) return <p className="muted">{empty}</p>;
  return (
    <>
      <div className="tbl-wrap">{children(rows)}</div>
      <nav className="pager" aria-label={w.listPages(what)}>
        <button type="button" className="btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          {w.listPrevious}
        </button>
        <span role="status">{w.listPageOf(page, pages, total, what)}</span>
        <button type="button" className="btn" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          {w.listNext}
        </button>
      </nav>
    </>
  );
}

/** A `YYYY-MM-DD` date as the Cockpit shows it, in the person's language: 27 Sept 2026. */
export function dayOf(date: string | null, language: Language = "en"): string {
  if (date === null) return "—";
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? date
    : formatDate(parsed, language, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}
